<template>
  <div
    class="checkpoint-level-banner"
    :class="[
      `checkpoint-level-banner--${levelSlug}`,
      {
        'checkpoint-level-banner--no-criteria': !hasCriteria,
        'checkpoint-level-banner--open': isOpen,
        'checkpoint-level-banner--disabled': disabled,
      },
    ]"
  >
    <div
      ref="levelTrigger"
      class="checkpoint-level-banner__selector"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-disabled="disabled ? 'true' : undefined"
      @click.stop="toggleDropdown"
      @keydown.enter.prevent.stop="toggleDropdown"
      @keydown.space.prevent.stop="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <span class="checkpoint-level-banner__selector-label"
        >{{ internalLevel }}{{ selectorLabelSuffix }}</span
      >
      <v-icon
        class="checkpoint-level-banner__chevron"
        :class="{ 'checkpoint-level-banner__chevron--open': isOpen }"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <div v-if="hasCriteria" class="checkpoint-level-banner__content" :title="prefix_with_criteria">
      {{ prefix_with_criteria }}
    </div>

    <div v-if="isOpen" ref="levelDropdown" class="checkpoint-level-banner__dropdown">
      <ul class="checkpoint-level-banner__menu" role="listbox">
        <li
          v-for="opt in ['Apprentice', 'Professional', 'Coach', 'Master']"
          :key="opt"
          class="checkpoint-level-banner__menu-item"
          :class="{
            [`checkpoint-level-banner__menu-item--${slugify(opt)}`]: true,
            'checkpoint-level-banner__menu-item--selected': opt === internalLevel,
          }"
          role="option"
          :aria-selected="opt === internalLevel"
          @click.stop="selectLevel(opt)"
        >
          <div class="checkpoint-level-banner__menu-item-row">
            <span class="checkpoint-level-banner__menu-label">{{ opt }}</span>
            <svg
              v-if="opt === internalLevel"
              class="checkpoint-level-banner__check"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M16.6668 5L7.50016 14.1667L3.3335 10"
                stroke="#7F56D9"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * Mirrors `update:level` from CheckpointLevelBanner onto Storybook reactive `args`.
 */
export function syncCheckpointLevelStoryArgs(args, level) {
  if (args != null && level != null) {
    args.level = level;
  }
}

/** Module scope: safe to use in hoisted `defineProps()` (cannot use setup-local functions there). */
export function isCheckpointLevelValue(v) {
  return ["Apprentice", "Professional", "Coach", "Master"].includes(v);
}
</script>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  level: {
    type: String,
    default: "Apprentice",
    validator: (v) => ["Apprentice", "Professional", "Coach", "Master"].includes(v),
  },
  /** Appended to the level name in the trigger only (e.g. `" Level"` → “Apprentice Level”). */
  selectorLabelSuffix: {
    type: String,
    default: "",
  },
  prefix_with_criteria: {
    type: String,
    default: null,
  },
  /** Suppresses the level dropdown affordance (e.g. read-only rows). */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:level", "change"]);

const levelTrigger = ref(null);
const levelDropdown = ref(null);
const isOpen = ref(false);
const internalLevel = ref(props.level);

const hasCriteria = computed(
  () => props.prefix_with_criteria != null && props.prefix_with_criteria !== ""
);

const levelSlug = computed(() => slugify(internalLevel.value));

function slugify(level) {
  return String(level || "Apprentice")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

watch(
  () => props.level,
  (v) => {
    if (isCheckpointLevelValue(v)) {
      internalLevel.value = v;
    }
  }
);

watch(
  () => props.disabled,
  (isDisabled) => {
    if (isDisabled) closeDropdown();
  }
);

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
}

function closeDropdown() {
  isOpen.value = false;
}

function updateLevel(nextLevel) {
  if (!isCheckpointLevelValue(nextLevel)) return;
  internalLevel.value = nextLevel;
  emit("update:level", nextLevel);
  emit("change", nextLevel);
}

function selectLevel(opt) {
  updateLevel(opt);
  closeDropdown();
}

function handleClickOutside(event) {
  const inTrigger = levelTrigger.value?.contains(event.target);
  const inDropdown = levelDropdown.value?.contains(event.target);
  if (!inTrigger && !inDropdown) {
    closeDropdown();
  }
}

onMounted(() => {
  setTimeout(() => {
    document.addEventListener("click", handleClickOutside);
  }, 0);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.checkpoint-level-banner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 5px 12px;
  min-height: 0;
  border-radius: 8px;
  border: 1px solid;
  font-family: Inter, sans-serif;
  overflow: visible;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;
}

.checkpoint-level-banner--open {
  z-index: 1000;
}

/* —— Themes (whole banner — single surface per level) —— */
.checkpoint-level-banner--apprentice {
  background-color: #fff5d3;
  border-color: #e58700;
  color: #e58700;
}

.checkpoint-level-banner--professional {
  background-color: #f3fff9;
  border-color: #00b813;
  color: #00b813;
}

.checkpoint-level-banner--coach {
  background-color: #effeff;
  border-color: #00a3ff;
  color: #00a3ff;
}

.checkpoint-level-banner--master {
  background-color: #f2f3ff;
  border-color: #2e1ec1;
  color: #2e1ec1;
}

.checkpoint-level-banner__selector {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 0;
  cursor: pointer;
  border-radius: 0;
  outline: none;
  background: transparent;
}

.checkpoint-level-banner--no-criteria .checkpoint-level-banner__selector {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20px;
  gap: 4px;
  flex: 1 1 auto;
  max-width: none;
  width: 100%;
  min-width: 0;
}

.checkpoint-level-banner__selector:focus-visible {
  box-shadow: 0 0 0 3px rgba(158, 119, 237, 0.35);
}

.checkpoint-level-banner__selector-label {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.checkpoint-level-banner--apprentice .checkpoint-level-banner__selector-label {
  color: #e58700;
}

.checkpoint-level-banner--professional .checkpoint-level-banner__selector-label {
  color: #00b813;
}

.checkpoint-level-banner--coach .checkpoint-level-banner__selector-label {
  color: #00a3ff;
}

.checkpoint-level-banner--master .checkpoint-level-banner__selector-label {
  color: #2e1ec1;
}

.checkpoint-level-banner__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 20px !important;
  color: #667085 !important;
  transition: transform 0.2s ease;
}

.checkpoint-level-banner__chevron :deep(.v-icon),
.checkpoint-level-banner__chevron :deep(i) {
  color: #667085 !important;
}

.checkpoint-level-banner__chevron--open {
  transform: rotate(180deg);
}

.checkpoint-level-banner--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.checkpoint-level-banner--disabled .checkpoint-level-banner__selector {
  cursor: default;
}

.checkpoint-level-banner__content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkpoint-level-banner__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1001;
  display: flex;
  width: 100%;
  min-width: 140px;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow:
    0 12px 16px -4px rgba(16, 24, 40, 0.08),
    0 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.checkpoint-level-banner__menu {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-sizing: border-box;
}

.checkpoint-level-banner__menu-item {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.checkpoint-level-banner__menu-item-row {
  display: flex;
  padding: 0 10px 0 8px;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  min-height: 22px;
  box-sizing: border-box;
  justify-content: space-between;
}

.checkpoint-level-banner__menu-item:hover:not(.checkpoint-level-banner__menu-item--selected) {
  background-color: #f9fafb;
}

.checkpoint-level-banner__menu-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.checkpoint-level-banner__menu-item--apprentice .checkpoint-level-banner__menu-label {
  color: #e58700;
}

.checkpoint-level-banner__menu-item--professional .checkpoint-level-banner__menu-label {
  color: #00b813;
}

.checkpoint-level-banner__menu-item--coach .checkpoint-level-banner__menu-label {
  color: #00a3ff;
}

.checkpoint-level-banner__menu-item--master .checkpoint-level-banner__menu-label {
  color: #2e1ec1;
}

.checkpoint-level-banner__menu-item--selected.checkpoint-level-banner__menu-item--apprentice {
  background-color: #fff5d3;
}

.checkpoint-level-banner__menu-item--selected.checkpoint-level-banner__menu-item--professional {
  background-color: #f3fff9;
}

.checkpoint-level-banner__menu-item--selected.checkpoint-level-banner__menu-item--coach {
  background-color: #effeff;
}

.checkpoint-level-banner__menu-item--selected.checkpoint-level-banner__menu-item--master {
  background-color: #f2f3ff;
}

.checkpoint-level-banner__check {
  flex-shrink: 0;
}
</style>
